import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import axios from 'axios'
import cl from 'classnames'

import { formatChatMessageLinks } from '@/src/components/ChatEntry'
import { Modal } from '@/src/components/ui/Modal/Modal'

import { useRoomContext } from '@/src/context'
import { IMessageProps } from '@/src/types/chat.type'

import { useClickEsc } from '@/src/hooks/useClickEsc.hook'
import { useClickOutside } from '@/src/hooks/useClickOutside.hook'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

import { BASE_URL_MEET } from '@/src/constants/env'

import styles from './Message.module.css'

export const Message = ({
  author,
  message,
  id,
  showMetaData,
  isLocalMessage,
  createdAtTitle,
  created_at,
  updated_at,
}: IMessageProps) => {
  const { t } = useTranslation()
  const room = useRoomContext()

  const messageTextRef = useRef<HTMLSpanElement | null>(null)
  const messageRef = useRef<HTMLLIElement | null>(null)
  const hintsListRef = useRef<HTMLUListElement | null>(null)

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false)
  const [isOpenHints, setIsOpenHints] = useState<boolean>(false)

  const { primary_color: background, text_primary_color: color } = useAppSelector(selectBranding)

  const hasBeenEdited = created_at !== updated_at

  // Эта функция нужна для того, чтобы если какой-то пользователь перезашел в комнату с новым именем, то подтянулось его новое имя, а если пользователя, который написал сообщение нет в списке участников в комнате, тогда подтянется его имя, которое было при отправке сообщения
  const getAuthorName = () => room?.remoteParticipants?.get(author?.id)?.name ?? author?.name

  const handleContextMenu = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event?.stopPropagation()
    event?.preventDefault()
  }

  const handleRightClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event?.stopPropagation()
    event?.preventDefault()

    if (event?.button === 2) {
      setIsOpenHints(true)
    }
  }

  const handleCloseHints = useCallback(() => {
    setIsOpenHints(false)
  }, [])

  useClickEsc(isOpenHints, setIsOpenHints)
  useClickOutside(messageTextRef, handleCloseHints, isOpenHints)

  const handleEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event?.stopPropagation()
    event?.preventDefault()
  }

  const handleDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event?.stopPropagation()
    event?.preventDefault()

    setIsOpenDeleteModal(true)
  }

  const confirmToDeleteMsg = async () => {
    const roomId: string | null = await room?.getSid()?.catch((err) => {
      console.error('Error with getting identity of room: \n', err)
      return null
    })

    if (!roomId || typeof roomId !== 'string') return []

    await axios.delete(
      `${BASE_URL_MEET}/api/chat/messages?roomName=${room?.name}&roomId=${roomId}&messageId=${id}`,
    )
  }

  return (
    <>
      <li
        ref={messageRef}
        // onContextMenu={handleContextMenu}
        className={cl(styles?.message, 'lk-chat-entry')}
        data-lk-message-origin={isLocalMessage ? 'local' : 'remote'}
      >
        {showMetaData && (
          <span className="lk-meta-data message-metadata">
            <strong style={{ color }} className="lk-participant-name">
              {getAuthorName()}
            </strong>

            <span className="lk-timestamp">
              {hasBeenEdited && t('chat.edited')}
              {createdAtTitle}
            </span>
          </span>
        )}

        <span
          ref={messageTextRef}
          className="lk-message-body"
          // onMouseDown={handleRightClick}
          style={isLocalMessage ? {} : { background }}
        >
          {message ? formatChatMessageLinks(message) : ''}
        </span>

        {/* <AnimatePresence>
          {isOpenHints && (
            <motion.ul
              ref={hintsListRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{
                bottom: messageRef?.current?.offsetHeight + hintsListRef?.current?.offsetHeight,
              }}
              className={styles?.hintList}
            >
              <motion.li
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className={styles?.hintItem}
              >
                <button onClick={handleEdit} className={styles?.hintBtn}>
                  <Image src="/images/edit.svg" alt="Edit icon" width={16} height={16} />
                  {t('chat.hintEdit')}
                </button>
              </motion.li>

              <motion.li
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className={cl(styles?.hintItem, styles?.dangerHint)}
              >
                <button onClick={handleDelete} className={styles?.hintBtn}>
                  <Image src="/images/delete.svg" alt="Delete icon" width={16} height={16} />
                  {t('chat.hintDelete')}
                </button>
              </motion.li>
            </motion.ul>
          )}
        </AnimatePresence> */}
      </li>

      <Modal
        isOpen={isOpenDeleteModal}
        onCancel={() => setIsOpenDeleteModal(false)}
        title={t('chat.titleToDeleteMsg')}
        footer={
          <>
            <button
              className="lk-disconnect-button"
              style={{ marginRight: '10px' }}
              onClick={confirmToDeleteMsg}
            >
              {t('chat.deleteMsg')}
            </button>

            <button className="lk-button" onClick={() => setIsOpenDeleteModal(false)}>
              {t('chat.dontDeleteMsg')}
            </button>
          </>
        }
      >
        {t('chat.contentToDeleteMsg')}
      </Modal>
    </>
  )
}
